import { filter, get, omit, pick, includes, without, toUpper, startsWith, toLower } from "lodash"
import moment from "moment"
import { createReducer } from "reduxsauce"
import { takeLatest, put, select, all, call, getContext } from "redux-saga/effects"
import { trackPortfolioLoaded } from "modules/portfolio-root/helpers"
import createActions from "helpers/actionCreator"
import { Types as LoaderTypes, Creators as DataCreators } from "model/data"
import { Types as PreferencesTypes } from "model/preferences"
import { getStartDate, getSelectedDate } from "selectors/dates"
import { getUserPreference } from "helpers/userPreferences"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getSelectedBase, getPreviousModelBaseUsed } from "modules/portfolio-root/selectors/base"
import { getTopicSummaryDate } from "modules/research-topic/selectors/dates"
import { AGGREGATION_FUNCTIONS } from "helpers/factorClassification"
import { getSelectedAccount } from "modules/account-root/selectors"
import { VIEW_TYPES, addBaseToHistory } from "helpers/base"
import { ONE_YEAR } from "helpers/dates"
import { get as apiGet } from "api"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { Creators as PreferencesCreators } from "model/preferences"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as compositeSecurityCreators } from "model/compositeSecurity"
import { handleUpdateRecentInstruments } from "../../../sagas/app/recentInstruments"
import { getPortfolioName, getPortfolioId, getFavoriteFactors, getSelectedModelId, getRawSelectedViews, getPeriod } from "./selectors/portfolios"
import { getBaseHistory, computeDefaultSelectedBase } from "./selectors/base"
import { getControls } from "./selectors/links"
import { getSecurity, getFactorId } from "./selectors"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}
export const FAVORITE_FACTORS_TYPE = "favoriteFactors"

function getSelectedBasePreference(state, newSelectedBase, newHistory) {
  const modelId = getSelectedModelId(state)
  const portfolioUUID = getPortfolioId(state)

  if (!newSelectedBase) {
    return [{
      type: "viewType",
      value: VIEW_TYPES.ABSOLUTE,
      noAccountId: true,
    }]
  }

  const userPreferencesItems = [{
    type: "portfolioBenchmarkHistory",
    key: `${portfolioUUID}:${modelId}`,
    value: newHistory,
  }, {
    type: "viewType",
    value: VIEW_TYPES.ACTIVE,
    noAccountId: true,
  }]

  return userPreferencesItems
}

function getAggregationPreferenceKey(state) {
  const selectedModel = getSelectedModelId(state)
  return `${get(state, "global.selected.portfolio")}${selectedModel}`
}

export const { Types, Creators } = createActions("ompnt/modules/portfolioRoot", {
  setPeriod: {
    props: ["period"],
    updatePreference: {
      getItems: (state, { period }) => ([{
        type: "lastSelectedPeriod",
        value: period,
        key: getPortfolioId(state),
      }]),
    },
  },
  setDate: ["date"],
  setProfileDate: ["profileDate"],
  setProfilePeriod: ["profilePeriod"],
  resetProfileDate: [],
  resetDate: [],
  resetPeriod: [],
  setSelectedBase: ["selectedBase", "modelId"],
  saveSelectedBase: {
    props: ["selectedBase", "newHistory"],
    track: false,
    updatePreference: {
      getItems: (state, { selectedBase, newHistory }) => getSelectedBasePreference(state, selectedBase, newHistory),
    },
  },
  setSelectedBaseSuccess: {
    props: [],
    track: false,
  },
  baseSearch: {
    props: ["search"],
    track: false,
  },
  baseInputFocus: {
    props: [],
    track: false,
  },
  baseInputBlur: {
    props: [],
    track: false,
  },
  openCompositeSecurity: {
    props: ["security"],
    track: false,
  },
  loadBaseHistory: {
    props: ["historyKey"],
    track: false,
  },
  toggleFavoriteFactor: {
    props: ["factorId", "modelId"],
    updatePreference: {
      getItems: (state, { modelId }) => [{
        type: FAVORITE_FACTORS_TYPE,
        value: getFavoriteFactors(state),
        key: modelId || getSelectedModelId(state),
      }],
    },
  },
  updatePortfolioModelSuccess: [],
  loadActiveBase: [],
  setFavoriteFactors: {
    props: ["favoriteFactors"],
    track: false,
  },
  setModifiedScreen: {
    props: ["value"],
    track: false,
  },
  removeModifiedScreen: {
    props: ["value"],
    track: false,
  },
  setPreviousModelSelectedBase: {
    props: ["previousSelectedBase"],
    track: false,
  },
  setModel: {
    props: ["modelId"],
    updatePreference: {
      getItems: (state, { modelId }) => [{
        type: "portfolioLastModelUsed",
        value: modelId,
        key: getPortfolioId(state),
      }],
    },
  },
  coverageNotification: [],
  setSnapshot: {
    props: ["snapshot"],
    track: false,
  },
  toggleShowNotHeld: {
    props: ["value"],
    track: true,
    trackType: "contributors-toggle-not-held",
    trackPayload: ({ value }) => ({ value }),
    updatePreference: {
      getItems: (state, { value }) => [{
        type: "analyzeTrendsContributors",
        value: { showNotHeld: value },
        key: getPortfolioId(state),
      }],
    },
  },
  addSelectedView: {
    props: ["factorId"],
    track: true,
    trackType: "analyze-trends-select-view-factor-add",
    trackPayload: ({ factorId }) => ({ factorId }),
    updatePreference: {
      getItems: (state) => [{
        type: "analyzeTrends",
        value: { selectedViews: getRawSelectedViews(state) },
        key: getPortfolioId(state),
      }],
    },
  },
  removeSelectedView: {
    props: ["factorId"],
    track: true,
    trackType: "analyze-trends-select-view-factor-removal",
    trackPayload: ({ factorId }) => ({ factorId }),
    updatePreference: {
      getItems: (state) => [{
        type: "analyzeTrends",
        value: { selectedViews: getRawSelectedViews(state) },
        key: getPortfolioId(state),
      }],
    },
  },
  setAggregationFn: {
    props: ["name"],
    updatePreference: {
      getItems: (state, { name }) => [{
        key: getAggregationPreferenceKey(state),
        type: "styleExposureAggregation",
        value: name,
      }],
    },
  },
  setSecurity: ["security"],
  setFactorId: ["factorId"],
  setExpansionMode: ["expand"],
  updateStateFromQueryString: ["queryStringState", "modelId", "account"],
}, { track: true })

export const INITIAL_STATE = {
  searchValue: "",
  baseDropdownVisible: false,
  baseHistory: EMPTY_ARRAY,
  date: null,
  profileDate: null,
  profilePeriod: ONE_YEAR,
  factorId: null,
  favoriteFactors: EMPTY_OBJECT,
  security: null,
  viewType: VIEW_TYPES.ABSOLUTE,
  selectedBase: null,
  previousModelSelectedBaseId: null,
  selectedModel: null,
  modifiedScreens: [],
  snapshot: null,
  showNotHeld: false,
  selectedViews: [],
  styleAggregation: AGGREGATION_FUNCTIONS.NONE,
}

export const handleLoadSuccess = (state, action) => {
  const { snapshot } = state
  if (action.resource === "portfolio") {
    return {
      ...state,
      date: null,
      profileDate: null,
      profilePeriod: ONE_YEAR,
      selectedBase: null,
      previousModelSelectedBaseId: null,
    }
  } else if (action.resource === "portfolioUserPreferences") {
    if (!snapshot) {
      const baseHistory = getUserPreference(action.data, "portfolioBenchmarkHistory", EMPTY_ARRAY)
      const viewType = getUserPreference(action.data, "viewType", VIEW_TYPES.ABSOLUTE)
      const lastModelUsed = getUserPreference(action.data, "portfolioLastModelUsed", null)
      const { showNotHeld } = getUserPreference(action.data, "analyzeTrendsContributors", {})
      const { selectedViews } = getUserPreference(action.data, "analyzeTrends", {})

      return {
        ...state,
        baseHistory,
        viewType,
        selectedModel: lastModelUsed,
        showNotHeld,
        selectedViews: selectedViews || [],
      }
    } else if (snapshot) {
      let base = {
        selectedModel: snapshot.modelId,
        snapshot: (state.selectedBase === snapshot.base && state.selectedModel === snapshot.modelId) ? null : snapshot,
      }
      if (snapshot.base) {
        base = {
          ...base,
          selectedBase: snapshot.base,
          viewType: VIEW_TYPES.ACTIVE,
        }
      }
      return { ...state, ...base }
    }
  } else if (action.resource === "styleExposureAggregationPreferences") {
    const styleAggregation = getUserPreference(action.data, "styleExposureAggregation")
    if (styleAggregation && !!AGGREGATION_FUNCTIONS[toUpper(styleAggregation)]) {
      return { ...state, styleAggregation }
    }
  } else {
    return state
  }
}

export function handleSetPeriod(state, { period }) {
  const newState = { period }

  if (state.date && (state.date < period.startDate || state.date > period.endDate)) {
    newState.date = null
  }

  return { ...state, ...newState }
}

export function handleSetDate(state, { date }) {
  return { ...state, date }
}

export function handleResetDate(state) {
  return { ...state, date: null }
}

export function handleSetProfilePeriod(state, { profilePeriod }) {
  return { ...state, profilePeriod }
}

export function handleSetProfileDate(state, { profileDate }) {
  return { ...state, profileDate }
}

export function handleResetProfileDate(state) {
  return { ...state, profileDate: null }
}

export function handleToggleFavoriteFactor(state, { factorId }) {
  let newFavorites = { ...state.favoriteFactors }

  if (!newFavorites[factorId]) {
    newFavorites[factorId] = true
  } else {
    newFavorites = omit(newFavorites, factorId)
  }

  return { ...state, favoriteFactors: newFavorites }
}

function handleBaseSearch(state, { search }) {
  return { ...state, searchValue: search }
}

function handleSaveSelectedBase(state, { selectedBase, newHistory }) {
  const newState = {
    selectedBase,
    baseDropdownVisible: false,
    viewType: selectedBase ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE,
    searchValue: "",
  }

  if (selectedBase) {
    return { ...state, ...newState, baseHistory: newHistory }
  }

  return { ...state, ...newState }
}

function handleBaseInputFocus(state) {
  return { ...state, baseDropdownVisible: true }
}

function handleBaseInputBlur(state) {
  return {
    ...state,
    baseDropdownVisible: false,
    searchValue: "",
  }
}

function handleUpdatePortfolioModelSuccess(state) {
  localStorage.removeItem(state.historyKey)

  return {
    ...state,
    selectedBase: null,
    historyKey: null,
    baseDropdownVisible: false,
    searchValue: "",
    viewType: VIEW_TYPES.ABSOLUTE,
  }
}

function handleSetSelectedBaseSuccess(state) {
  return { ...state }
}

function handleSetFavoriteFactors(state, { favoriteFactors }) {
  return { ...state, favoriteFactors }
}

function handleSetModel(state, { modelId }) {
  return {
    ...state,
    selectedModel: modelId,
    ...(state.selectedBase && { previousModelSelectedBaseId: state.selectedBase }),
    date: null,
    profileDate: null,
    selectedBase: null,
    modifiedScreens: [],
  }
}

function handleSetSnapshot(state, { snapshot }) {
  return { ...state, snapshot }
}

function handleSetPreviousModelSelectedBase(state, { previousSelectedBase }) {
  return { ...state, previousModelSelectedBaseId: pick(previousSelectedBase, ["id", "type", "experimentId"]) }
}

function handleSetModifiedScreen(state, { value }) {
  if (!includes(state.modifiedScreens, value)) {
    return { ...state, modifiedScreens: [...state.modifiedScreens, value] }
  }
  return state
}

function handleRemoveModifiedScreen(state, { value }) {
  if (includes(state.modifiedScreens, value)) {
    return { ...state, modifiedScreens: without(state.modifiedScreens, value) }
  }
  return state
}

function handleToggleShowNotHeld(state, { value }) {
  return { ...state, showNotHeld: value }
}

function handleResetPeriod(state) {
  return {
    ...state,
    period: INITIAL_STATE.period,
    date: INITIAL_STATE.date,
  }
}

function handleAddSelectedView(state, { factorId }) {
  return {
    ...state,
    selectedViews: [...state.selectedViews, factorId],
  }
}

function handleRemoveSelectedView(state, { factorId }) {
  const newSelectedViews = filter(state.selectedViews, item => item !== factorId)
  return {
    ...state,
    selectedViews: newSelectedViews,
  }
}

function handleSetAggregationFn(state, { name }) {
  return { ...state, styleAggregation: name }
}

function handleSetSecurity(state, { security }) {
  return { ...state, security }
}

function handleSetFactorId(state, { factorId }) {
  return { ...state, factorId }
}

function handleSetExpansionMode(state, { expand }) {
  return { ...state, expand }
}

export const HANDLERS = {
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [Types.SET_DATE]: handleSetDate,
  [Types.SET_PROFILE_DATE]: handleSetProfileDate,
  [Types.RESET_DATE]: handleResetDate,
  [Types.RESET_PROFILE_DATE]: handleResetProfileDate,
  [Types.SET_PROFILE_PERIOD]: handleSetProfilePeriod,
  [Types.SET_PERIOD]: handleSetPeriod,
  [Types.SAVE_SELECTED_BASE]: handleSaveSelectedBase,
  [Types.SET_SELECTED_BASE_SUCCESS]: handleSetSelectedBaseSuccess,
  [Types.BASE_SEARCH]: handleBaseSearch,
  [Types.BASE_INPUT_FOCUS]: handleBaseInputFocus,
  [Types.BASE_INPUT_BLUR]: handleBaseInputBlur,
  [Types.TOGGLE_FAVORITE_FACTOR]: handleToggleFavoriteFactor,
  [Types.UPDATE_PORTFOLIO_MODEL_SUCCESS]: handleUpdatePortfolioModelSuccess,
  [Types.SET_FAVORITE_FACTORS]: handleSetFavoriteFactors,
  [Types.SET_MODEL]: handleSetModel,
  [Types.SET_PREVIOUS_MODEL_SELECTED_BASE]: handleSetPreviousModelSelectedBase,
  [Types.SET_MODIFIED_SCREEN]: handleSetModifiedScreen,
  [Types.REMOVE_MODIFIED_SCREEN]: handleRemoveModifiedScreen,
  [Types.SET_SNAPSHOT]: handleSetSnapshot,
  [Types.TOGGLE_SHOW_NOT_HELD]: handleToggleShowNotHeld,
  [Types.RESET_PERIOD]: handleResetPeriod,
  [Types.ADD_SELECTED_VIEW]: handleAddSelectedView,
  [Types.REMOVE_SELECTED_VIEW]: handleRemoveSelectedView,
  [Types.SET_AGGREGATION_FN]: handleSetAggregationFn,
  [Types.SET_SECURITY]: handleSetSecurity,
  [Types.SET_FACTOR_ID]: handleSetFactorId,
  [Types.SET_EXPANSION_MODE]: handleSetExpansionMode,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* alertSetDate({ date }) {
  if (date.adjusted) {
    yield put(notificationCreators.add("info", null, `The selected date has been adjusted to the nearest available date: ${date.adjusted}.`))
  }
}

function* setSelectedBase({ selectedBase, modelId }) {
  const { earliestDate, latestDate, portfolioName, startDate, baseHistory, account } = yield select(state => ({
    earliestDate: getEarliestDate(state),
    portfolioName: getPortfolioName(state),
    latestDate: getLatestDate(state),
    baseHistory: getBaseHistory(state),
    startDate: getStartDate(state),
    account: state.global.selected.account,
  }))

  if (selectedBase) {
    yield put(Creators.saveSelectedBase(selectedBase, addBaseToHistory(baseHistory, selectedBase)))
    yield put(Creators.setSelectedBaseSuccess())

    const baseStartDate = get(selectedBase, "availableFrom")

    if (startDate < baseStartDate && earliestDate < baseStartDate) {
      yield put(notificationCreators.add("info", null, `Active Comparison: Now showing active metrics for ${portfolioName} against ${selectedBase.name} using dates: ${moment(baseStartDate).format("ll")} to ${moment(latestDate).format("ll")}.`))
    } else {
      yield put(notificationCreators.add("success", null, `Active Comparison: Now showing active metrics for ${portfolioName} against ${selectedBase.name}.`))
    }
    yield call(handleUpdateRecentInstruments, account, selectedBase.id, toLower(selectedBase.type), RECENT_INSTRUMENT_SEARCH_TYPES.BASE, { modelId })
  } else {
    yield put(Creators.saveSelectedBase(selectedBase))
    yield put(Creators.setSelectedBaseSuccess())
    yield put(notificationCreators.add("success", null, `Absolute Metrics: Now showing absolute metrics for ${portfolioName}.`))
  }
}

function* activeNotification() {
  const { selectedBase, earliestDate, latestDate, startDate, portfolioName, defaultSelectedBase, controls, previousModelBase } = yield select(state => ({
    selectedBase: getSelectedBase(state),
    defaultSelectedBase: computeDefaultSelectedBase(state),
    earliestDate: getEarliestDate(state),
    latestDate: getLatestDate(state),
    portfolioName: getPortfolioName(state),
    startDate: getStartDate(state),
    controls: getControls(state),
    previousModelBase: getPreviousModelBaseUsed(state),
  }))
  const currentBase = selectedBase || previousModelBase || defaultSelectedBase
  if (currentBase) {
    yield put(Creators.setPreviousModelSelectedBase(currentBase))

    if (controls.viewTypeSelector) {
      const baseStartDate = get(currentBase, "availableFrom")
      if (startDate < baseStartDate && earliestDate < baseStartDate) {
        yield put(notificationCreators.add("info", null, `Active Comparison: Now showing active metrics for ${portfolioName} against ${currentBase.name} using dates: ${moment(baseStartDate).format("ll")} to ${moment(latestDate).format("ll")}.`))
      } else {
        yield put(notificationCreators.add("success", null, `Active Comparison: Now showing active metrics for ${portfolioName} against ${currentBase.name}.`))
      }
    }
  }
}

function* loadSuccess(action) {
  if (action.resource === "userFavoriteFactors") {
    const { portfolioUUID, modelId, accountId } = yield select(state => ({
      portfolioUUID: getPortfolioId(state),
      modelId: getSelectedModelId(state),
      accountId: state.global.selected.accountId,
    }))

    let favoriteFactors = getUserPreference(action.data, "favoriteFactors")

    if (!favoriteFactors) {
      const favoriteFactorsStoreKey = `${portfolioUUID}_favoriteFactors`
      const localStorageFavoriteFactors = JSON.parse(localStorage.getItem(favoriteFactorsStoreKey))

      if (localStorageFavoriteFactors) {
        favoriteFactors = localStorageFavoriteFactors
        yield put(PreferencesCreators.savePreferences([FAVORITE_FACTORS_TYPE, favoriteFactors, modelId, accountId]))
        localStorage.removeItem(favoriteFactorsStoreKey)
      }
    }

    yield put(Creators.setFavoriteFactors(favoriteFactors || {}))
  } else if (action.resource === "portfolio") {
    yield put(Creators.resetPeriod())

    // Track portfolio loaded
    const [portfolioId, portfolioName] = yield all([
      select(getPortfolioId),
      select(getPortfolioName),
    ])
    const pathname = get(window, "location.pathname", null)
    trackPortfolioLoaded(portfolioId, portfolioName, pathname)
  }
}

function* coverageNotification() {
  const { controls } = yield select(state => ({
    controls: getControls(state),
  }))
  if (controls.viewTypeSelector) {
    yield put(notificationCreators.add("warning", null, "There is less than 50% model coverage for this comparison instrument. The active metrics may not be accurate"))
  }
}

// Export it to be able to test it
export function* handleNavigate(action) {
  const newFactorId = get(action, "location.query.factor", null)
  let newSecurity = get(action, "location.query.security", null)
  const [date, account, security, factorId, period, topicSummaryDate] = yield all([
    select(getSelectedDate),
    select(getSelectedAccount),
    select(getSecurity),
    select(getFactorId),
    select(getPeriod),
    select(getTopicSummaryDate),
  ])

  if (newFactorId !== factorId) {
    yield put(Creators.setFactorId(newFactorId))
  }

  if (newSecurity) {
    const parts = newSecurity.split(".")
    if (parts.length >= 2) {
      const exchange = parts.shift()
      const ticker = parts.join(".")

      newSecurity = {
        exchange,
        ticker,
      }
    } else {
      newSecurity = null
    }
  }

  if (newSecurity !== security) {
    yield put(Creators.setSecurity(newSecurity))
  }

  if (newFactorId !== factorId || newSecurity !== security) {
    if (!newFactorId && !newSecurity) {
      yield put(Creators.setProfileDate(null))
      yield put(Creators.setProfilePeriod("1Y"))
    } else if (!factorId && !security && (newFactorId || newSecurity)) {
      const pathname = get(action, "location.pathname", null)

      let newProfileDate = null
      if (startsWith(pathname, `/${account}/p`)) {
        newProfileDate = date
      } else if (startsWith(pathname, `/${account}/t`)) {
        newProfileDate = topicSummaryDate
      }

      yield put(Creators.setProfileDate(newProfileDate))
      yield put(Creators.setProfilePeriod(period))
    }
  }
}

function* handleRedirect(action) {
  const { account } = yield select(state => ({
    account: get(state, "global.selected.account"),
  }))

  const { navigate } = yield getContext("router")
  if (action.error.code === 400 && action.error.errors === "no_dataset") {
    yield call(navigate, `/${account}/home/resources`, { replace: true })
    yield put(notificationCreators.add("error", null, "You have been redirected because you do not have access to any risk models."))
  } else if (action.error.code === 404) {
    yield call(navigate, `/${account}/home/resources`, { replace: true })
    yield put(notificationCreators.add("error", null, "You have been redirected because you do not have access to the resource or it no longer exists."))
  }
}

function isPortfolioLoadError(action) {
  return action.type === LoaderTypes.LOAD_ERROR
    && action.resource === "portfolio"
}

function* handleOpenCompositeSecurity(action) {
  const { modelId, account } = yield select(state => ({
    modelId: getSelectedModelId(state),
    account: state.global.selected.account,
  }))
  yield put(compositeSecurityCreators.openCompositeSecurity(account, action.security, true, modelId))
}

function* getBaseWithMetadata(base) {
  const { account, modelId, latestDate } = yield select(state => ({
    account: state.global.selected.account,
    modelId: getSelectedModelId(state),
    latestDate: getLatestDate(state),
  }))

  const { id, type } = base
  let baseToSet = { ...base }
  try {
    const baseMetadata = yield call(apiGet, `/api/${account}/m/${modelId}/${type}/${id}/metadata?${new URLSearchParams({ ...pick(base, "experimentId"), date: latestDate })}`)
    baseToSet = { ...baseToSet, ...baseMetadata }
    // An error here means that something is wrong with the base parameters provided or the user doesn't not have access
    // to the base. In both cases, we don't want to set the base.
  } catch {
    return null
  }
  return baseToSet
}

function* handleUpdateStateFromQueryString({ queryStringState, modelId, account }) {
  if (queryStringState.modelId) {
    yield put(Creators.setModel(queryStringState.modelId))
  }

  if (queryStringState.base) {
    const baseToSet = yield call(getBaseWithMetadata, queryStringState.base)
    yield put(Creators.setSelectedBase(baseToSet, modelId))
    yield call(handleUpdateRecentInstruments, account, baseToSet.id, toLower(baseToSet.type), RECENT_INSTRUMENT_SEARCH_TYPES.BASE, { modelId })
  }
}

function* handleUpdatePreferencesSuccess({ action }) {
  const { type } = action || {}

  switch (type) {
    case Types.TOGGLE_FAVORITE_FACTOR:
      yield put(DataCreators.resetCollections(["userFavoriteFactors"]))
      break
  }
}

export function* saga() {
  yield takeLatest(isPortfolioLoadError, handleRedirect)
  yield takeLatest(Types.SET_SELECTED_BASE, setSelectedBase)
  yield takeLatest(Types.SET_DATE, alertSetDate)
  yield takeLatest(Types.LOAD_ACTIVE_BASE, activeNotification)
  yield takeLatest(Types.COVERAGE_NOTIFICATION, coverageNotification)
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, loadSuccess)
  yield takeLatest(HistoryTypes.NAVIGATE, handleNavigate)
  yield takeLatest(Types.OPEN_COMPOSITE_SECURITY, handleOpenCompositeSecurity)
  yield takeLatest(Types.UPDATE_STATE_FROM_QUERY_STRING, handleUpdateStateFromQueryString)
  yield takeLatest(PreferencesTypes.UPDATE_PREFERENCES_SUCCESS, handleUpdatePreferencesSuccess)
}
